import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CurrencyPipe } from "@angular/common";
import { Observable, Subscription, of } from "rxjs";
import { tap, catchError, map, finalize } from "rxjs/operators";

//import { ColDef, GridOptions } from '@ag-grid-enterprise/all-modules';
import { ColDef, GridOptions, GridApi } from "ag-grid-community";

import { AuthService } from "../../../services/auth.service";

import { FeeAdministrationService } from "../../services/fee-administration.service";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { SubHeaderService } from "../../../core/services/sub-header.service";
import { LoaderService } from "../../../common/services/loader.service";
import { SubHeader } from "../../../core/models/sub-header.model";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";

import { FeeEditorComponent } from "./fee-editor/fee-editor.component";
import { EditableGroupRowRendererComponent } from "../../../common/components/ag-grid/editable-group-row-renderer.component";
import { CanDeactivateComponent } from "../../../common/classes/can-deactivate.component";


import { FeeConfig, UpdatedFeeRequest } from "../../models/fee.model";

@Component({
    selector: 'app-manage-fees',
    templateUrl: './manage-fees.component.html',
    styleUrls: ['./manage-fees.component.scss'],
    providers: [CurrencyPipe],
    standalone: false
})
export class ManageFeesComponent extends CanDeactivateComponent implements OnInit, OnDestroy {

	private subscriptions: Subscription = new Subscription();

	config: UpdatedFeeRequest[];
	updatedData: UpdatedFeeRequest[] = [];

	completeDialog: Dialog = new Dialog("Fees have been updated.", [new DialogButton("OK", false, () => this.resetUpdates())]);

	noUpdates: Dialog = new Dialog("There are no pending updates to save.", [new DialogButton("OK", false, () => { return; })]);

	@ViewChild(FeeEditorComponent) public editor: FeeEditorComponent;

	get excelStyles(): any {
		return this._formatter.excelStyles;
	}

	feeData: Observable<FeeConfig[]>;

	gridOptions: GridOptions;
	gridApi?: GridApi;


	subheader: SubHeader = new SubHeader({
		title: "Manage Fees", buttons: [{ name: "Save Updates", method: () => this.save() }] });


	statusBar = {
		statusPanels: [
			{
				statusPanel: 'agFilteredRowCountComponent',
				align: 'right',
			},
			{
				statusPanel: 'agTotalRowCountComponent',
				align: 'right',
			}
		]
	};



	constructor(private _adminService: FeeAdministrationService, _auth: AuthService, _dialog: DynamicDialogService, private _formatter: GridFormatterService, private _subheaderService: SubHeaderService,
		private _loader: LoaderService,	private currencyPipe: CurrencyPipe) {
		super(_dialog, _auth);
	}

	ngOnInit() {
		this.confirmDialog = new Dialog("<p class=\"bold\">You have made unsaved changes to the current request.  Are you sure you want to exit without saving?</p>", [new DialogButton("Exit Page", true, () => this.onLeaveConfirm()), new DialogButton("Cancel", false, () => { return; })])
		this.allowDeactivate.next(true);
		this.gridOptions = {
			defaultColDef: {
				filter: true,
				sortable: true
			},
			columnDefs: this.defineColumns(),
			editType: 'fullRow',
			onGridReady: (params) => {
				this.gridApi = params.api;
				this.gridApi.sizeColumnsToFit();
			},
			groupDisplayType: "groupRows",
			groupDefaultExpanded: -1,
			groupRowRenderer: EditableGroupRowRendererComponent,
			groupRowRendererParams: {
				suppressCount: true,
				action: (obj: any) => this.edit(obj),
				buttonText: "Edit",
				labelTextProperty: "displayName",
				boldLabel: true
			},
			getRowStyle: (params) => {
				if (params.node.group) {
					return { backgroundColor: '#D3C8C8' };
				}
				return undefined;
			},
			headerHeight: 40,
			rowHeight: 40,
			overlayLoadingTemplate: '<span class="ag-overlay-loading-center">Loading...</span>',
			getRowClass: (params: any) => {
				if (params.data != null && params.data['isDirty']) {
					return 'red';
				}
				return "";
			}			
		};

		this.feeData = this._adminService.getFeeConfiguration()
			.pipe(
				catchError((err) => {
					console.error(err);
					this.dialogService.showError();
					return of([]);
				}),
				tap((data: FeeConfig[]) => {
					this.config = [];
					for (let d = 0; d < data.length; d++) {
						let conf = new UpdatedFeeRequest(data[d]);
						this.config.push(conf);
					}
				})
		);
		this._subheaderService.updateSubHeader(this.subheader);
		let subs$: Subscription = this.feeData.subscribe({
			next: () => {
				
			},
			error: (err) => {
				console.error(err);
				this.dialogService.showError();
			}
		}
			
		);
		this.subscriptions.add(subs$);

	}

	ngOnDestroy() {
		if (this.subscriptions != null) {
			this.subscriptions.unsubscribe();
		}
	}

	defineColumns(): ColDef[] {
		let columns: ColDef[] = [];
		columns = [
			{
				headerClass: "wrapped",
				headerName: "Display Name",
				headerTooltip: "Display Name",
				field: "id",
				rowGroup: true,
				hide: true,
				//showRowGroup: true,
				minWidth: 300,
				maxWidth: 350,
				filter: false,
				sortable: false,
				
			} as ColDef,
			// {
			// 	headerClass: "wrapped",
			// 	headerName: "Display Name",
			// 	headerTooltip: "Display Name",
			// 	field: "displayName",
			// 	tooltipField: "displayName",		
			// 	minWidth: 125,
			// 	maxWidth: 125,
			// 	filter: false
			// } as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Effective From",
				headerTooltip: "Effective From",
				field: "effectiveFromDate",
				valueFormatter: this._formatter.formatDate("effectiveFromDate", "MM/dd/yyyy", "N/A", "+0000"),
				tooltipValueGetter: this._formatter.formatDate("effectiveFromDate", "MM/dd/yyyy", "N/A", "+0000"),
				filter: "agDateColumnFilter",
				filterParams: this._formatter.defaultDateFilterParams,
				minWidth: 150,
				maxWidth: 175
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Effective To",
				headerTooltip: "Effective To",
				field: "effectiveToDate",
				valueFormatter: this._formatter.formatDate("effectiveToDate", "MM/dd/yyyy", "No End Date", "+0000"),
				tooltipValueGetter: this._formatter.formatDate("effectiveToDate", "MM/dd/yyyy", "No End Date", "+0000"),
				filter: "agDateColumnFilter",
				filterParams: this._formatter.defaultDateFilterParams,
				minWidth: 150,
				maxWidth: 175
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Fee Amount",
				headerTooltip: "Fee Amount",
				field: "flatFee",
				valueFormatter: this.formatFlatFee(),
				tooltipValueGetter: this.formatFlatFee(), 
				filter: "agNumberColumnFilter",
				filterParams: this._formatter.defaultNumberFilterParams,
				minWidth: 150,
				maxWidth: 200
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Fee Matrix",
				headerTooltip: "Fee Matrix",
				valueGetter: this.formatFeeMatrix(),
				tooltipValueGetter: this.formatFeeMatrix(),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 150,
				maxWidth: 175
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Fee Types",
				headerTooltip: "Fee Types",
				valueGetter: this.formatFeeTypes(),
				tooltipValueGetter: this.formatFeeTypes(),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 150,
				maxWidth: 175
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Expedite Fee Amount",
				headerTooltip: "Expedite Fee Amount",
				field: "expediteFee",
				valueFormatter: this._formatter.formatCurrency("expediteFee"),
				tooltipValueGetter: this._formatter.formatCurrency("expediteFee"),
				filter: "agNumberColumnFilter",
				filterParams: this._formatter.defaultNumberFilterParams,
				minWidth: 150,
				maxWidth: 205
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Legal Reference",
				headerTooltip: "Legal Reference",
				field: "legalReference",
				tooltipField: "legalReference",
				valueFormatter: this._formatter.formatPotentiallyBlankField("legalReference", "N/A"),
				tooltipValueGetter: this._formatter.formatPotentiallyBlankField("legalReference", "N/A"),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 200,
				maxWidth: 275
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Department",
				headerTooltip: "Department",
				field: "department",
				tooltipField: "department",
				valueFormatter: this._formatter.formatPotentiallyBlankField("department", "N/A"),
				tooltipValueGetter: this._formatter.formatPotentiallyBlankField("department", "N/A"),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 135,
				maxWidth: 160
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Fund",
				headerTooltip: "Fund",
				field: "fund",
				tooltipField: "fund",
				valueFormatter: this._formatter.formatPotentiallyBlankField("fund", "N/A"),
				tooltipValueGetter: this._formatter.formatPotentiallyBlankField("fund", "N/A"),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 125,
				maxWidth: 150
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "ALI",
				headerTooltip: "ALI",
				field: "ali",
				tooltipField: "ali",
				valueFormatter: this._formatter.formatPotentiallyBlankField("ali", "N/A"),
				tooltipValueGetter: this._formatter.formatPotentiallyBlankField("ali", "N/A"),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 125,
				maxWidth: 150
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Program",
				headerTooltip: "Program",
				field: "program",
				tooltipField: "program",
				valueFormatter: this._formatter.formatPotentiallyBlankField("program", "N/A"),
				tooltipValueGetter: this._formatter.formatPotentiallyBlankField("program", "N/A"),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 125,
				maxWidth: 150
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Revenue Source Code",
				headerTooltip: "Revenue Source Code",
				field: "revenueSourceCode",
				tooltipField: "revenueSourceCode",
				valueFormatter: this._formatter.formatPotentiallyBlankField("revenueSourceCode", "N/A"),
				tooltipValueGetter: this._formatter.formatPotentiallyBlankField("revenueSourceCode", "N/A"),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 150,
				maxWidth: 175
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Reporting",
				headerTooltip: "Reporting",
				field: "reporting",
				tooltipField: "reporting",
				valueFormatter: this._formatter.formatPotentiallyBlankField("reporting", "N/A"),
				tooltipValueGetter: this._formatter.formatPotentiallyBlankField("reporting", "N/A"),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 125,
				maxWidth: 150
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Account Description",
				headerTooltip: "Account Description",
				field: "accountDescription",
				tooltipField: "accountDescription",
				valueFormatter: this._formatter.formatPotentiallyBlankField("accountDescription", "N/A"),
				tooltipValueGetter: this._formatter.formatPotentiallyBlankField("accountDescription", "N/A"),
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				minWidth: 150,
				maxWidth: 250
			} as ColDef
			
			

		] as ColDef[];

		return columns;
	}

	edit(rowId: string): void {
		if (this.editor != null) {
			let rows: FeeConfig[] = this.config.filter(c => c.id == rowId);
			this.editor.openForm(rows);
		}
	}


	formatFeeMatrix(): any {
		return (obj) => {
			let value: string = "";
			if (obj != null && obj.data != null && obj.data["feeMatrices"] != null) {
				if (obj.data["feeMatrices"].length) {
					for (var m = 0; m < obj.data["feeMatrices"].length; m++) {
						if (m != 0) {
							value = `${value},`
						}
						value = `${value} ${obj.data["feeMatrices"][m].feeCode}:${obj.data["feeMatrices"][m].amount}`;
					}
				}
			}
			return value;
		}
	}

	formatFeeTypes(): any {
		return (obj) => {
			let value: string = "";
			if (obj != null && obj.data != null && obj.data["feeTypes"] != null) {
				if (obj.data["feeTypes"].length) {
					for (var t = 0; t < obj.data["feeTypes"].length; t++) {
						if (t != 0) {
							value = `${value},`
						}
						value = `${value} ${obj.data["feeTypes"][t].feeType}`
					}
				}
			}
			return value;
		}
	}

	formatFlatFee(): any {
		return (obj) => {
			let value: string = "";
			if (obj != null && obj.data != null) {
				if (obj.data['flatFee'] != null)
					value = obj.data['flatFee'];
				if (obj.data['feeMatrices'] != null && obj.data["feeMatrices"].length) {
					value = 'Uses Fee Matrix';
				}
				else {
					value = this.currencyPipe.transform(value, 'USD', 'symbol', '1.2-2');
				}
			} else {
				if (obj.data['feeMatrices'] != null && obj.data["feeMatrices"].length) {
					value = 'Uses Fee Matrix';
				}
				else {
					value = this.currencyPipe.transform(value, 'USD', 'symbol', '1.2-2');
				}
			}
			return value;
		}
	}

	onLeaveConfirm(): void {
		this.allowDeactivate.next(true);
	}

	resetUpdates(): void {
		this.updatedData = [];
		this.allowDeactivate.next(true);
	}

	save(): void {
		if (this.updatedData != null && this.updatedData.length > 0) {
			this.updatedData.forEach(u => {
				if (u.identifier == null || u.identifier < 0) {
					u.identifier = 0;
				}
			}); //any 'updates', except for when the start date isn't changed, end up getting saved as 'adds', so set the identifier to 0 to allow entityframework to do its magic
			let obs$: Observable<UpdatedFeeRequest[]> = this._adminService.updateFeeConfiguration(this.updatedData).pipe(
				map((data: FeeConfig[]) => {
					this.config = [];
					for (let d = 0; d < data.length; d++) {
						let conf = new UpdatedFeeRequest(data[d]);
						this.config.push(conf);
					}
					return this.config;
				})
			);
			this._loader.toggleLoader(true);
			let subs$ = obs$
				.pipe(finalize(() => {
					this._loader.toggleLoader(false);
				}))
				.subscribe(
					() => {
						this.completeDialog.toggleDialog(true);
						this.dialogService.updateDialog(this.completeDialog);
					},
					(err) => {
						console.error(err);
						this.dialogService.showError();
					}
				);
			this.subscriptions.add(subs$);
		} else {
			this.noUpdates.toggleDialog(true);
			this.dialogService.updateDialog(this.noUpdates);
		}		
	}

	saveUpdate(newConfig: UpdatedFeeRequest): void {
		this.allowDeactivate.next(false);
		let fees = this.config.filter(f => f.id == newConfig.id).sort((a, b) => {
			if (a.effectiveFromDate < b.effectiveFromDate) return -1;
			if (b.effectiveFromDate < a.effectiveFromDate) return 1;
			if (a.effectiveToDate == null || b.effectiveToDate == null) return 0;
			if (a.effectiveToDate < b.effectiveToDate) return -1;
			if (a.effectiveToDate > b.effectiveToDate) return 1;
			return 0;
		});
		
		let doAdd: boolean = true;
		for (let f = 0; f < fees.length; f++) {
			let fee = fees[f];
			fee.effectiveFromDate = new Date(fee.effectiveFromDate);
			if (fee.identifier == newConfig.identifier) {
				fees.splice(f, 1, newConfig);
				doAdd = false;
			} else if (fee.effectiveFromDate < newConfig.effectiveFromDate && (fee.effectiveToDate == null || new Date(fee.effectiveToDate) > newConfig.effectiveFromDate)) {
				fee.effectiveToDate = newConfig.effectiveFromDate;
				fee.isDirty = true;
				fees.splice(f, 1);
				fees.push(fee);
			}		
		}
		fees = fees.filter(f => f.effectiveFromDate <= newConfig.effectiveFromDate); //remove any that were starting after the update that we're making
		
		if (doAdd) {
			if (newConfig.feeMatrices != null && newConfig.feeMatrices.length > 0) {
				newConfig.feeMatrices.forEach(m => m.id = 0); //any 'updates' end up getting saved as 'adds', so set the id to 0 to allow entityframework to do its magic, but only want to reset this if not reusing an existing one
			}
			if (newConfig.feeTypes != null && newConfig.feeTypes.length > 0) {
				newConfig.feeTypes.forEach(m => m.id = 0); //any 'updates' end up getting saved as 'adds', so set the id to 0 to allow entityframework to do its magic, but only want to reset this if not reusing an existing one
			}
			fees.push(newConfig);
		}
		
		this.config = this.config.filter(c => c.id != newConfig.id);
		this.config = this.config.concat(fees);

		this.config = this.config.sort((a, b) => {
			if (a.displayName > b.displayName) return 1;
			if (a.displayName < b.displayName) return -1;
			return 0;
		});
		if (this.updatedData.length == 0) {
			this.updatedData.push(newConfig);
		} else {
			let updateToUpdate = this.updatedData.findIndex(f => f.id == newConfig.id);
			if (updateToUpdate == -1) { //new change doesn't relate to other saved changes
				this.updatedData.push(newConfig);
			} else {
				this.updatedData.splice(updateToUpdate, 1, newConfig); //replace the existing update for this fee type with the new one
			}
		}
		this.gridApi.setGridOption("loading", true);
		this.gridApi.setGridOption("rowData", []);
		setTimeout(() => {
			this.gridApi.setGridOption("rowData", this.config);
			this.gridApi.setGridOption("loading", false);
		}, 1500);
	}
}
